import { useApiClient } from '~/api-client';
import { UnitResource, UnitStateData, UnitStatusUpdateResponse } from '@mark43/cad-api';
import { useMutation } from '@tanstack/react-query';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useReleaseUnit } from '../common/hooks/use-release-unit';
import { useUnitContextMenuQuery } from '../unit-context-menu/use-unit-context-menu-query';
import { useCallback } from 'react';

export function useTransitionUnitStatusMutation() {
    const drawerControls = useDrawerControls();
    const apiClient = useApiClient();
    const unitAssignment = useUnitAssignment();
    const unitId = unitAssignment?.unitId;
    const { data: unitDetails } = useUnitContextMenuQuery({
        unitId: unitId ?? -1,
        enabled: !!unitId,
    });

    const executeStatusUpdate = useCallback(
        (args: { unitStateData: UnitStateData; eventId?: number }, eventIdToUse?: number) => {
            if (!unitId) {
                return Promise.resolve(false);
            }

            return apiClient
                .request<UnitResource.UnitStatusUpdateFirstResponder>({
                    method: 'POST',
                    url: 'unit/status/first_responder/',
                    data: {
                        unitStatusUpdateDtos: [
                            {
                                unitId,
                                unitStateId: args.unitStateData.id,
                                agencyEventId: eventIdToUse,
                            },
                        ],
                    },
                })
                .then((data) => {
                    const ret = data.data.data;
                    if (ret) {
                        return ret;
                    }
                    return false;
                });
        },
        [apiClient, unitId]
    );

    const releaseUnit = useReleaseUnit({
        isOnlyUnitAssignedToEvent: unitDetails?.clickParams?.isOnlyUnitAssignedToEvent ?? false,
        assignedEventNumber: unitAssignment?.assignedEvent?.eventNumber,
        assignedEventLocation: unitDetails?.clickParams.assignedEventLocation,
        callSign: unitAssignment?.callSign,
    });

    const releaseUnitWithConfirmation = (
        args: { unitStateData: UnitStateData; eventId?: number },
        eventIdToUse?: number
    ) =>
        releaseUnit({
            callback: () => executeStatusUpdate(args, eventIdToUse),
        }).then((result) => result ?? Promise.resolve(false));

    return useMutation(
        (args: {
            unitStateData: UnitStateData;
            eventId?: number;
        }): Promise<false | UnitStatusUpdateResponse> => {
            const assignedEventId = unitAssignment?.assignedEvent?.eventId;
            const eventIdToUse = args.eventId ?? assignedEventId;
            const { unitStateData } = args;
            if (!unitId) return Promise.resolve(false);
            switch (unitStateData.unitStateGroup) {
                case 'ADMIN':
                case 'SERVICE_ASSIGNMENT':
                    drawerControls.open({
                        type: 'status-admin',
                        props: { unitId, statusId: unitStateData.id },
                    });
                    return Promise.resolve(false);
                case 'DIRECTED_PATROL':
                    drawerControls.open({
                        type: 'status-directed-patrol',
                        props: { unitId, statusId: unitStateData.id },
                    });
                    return Promise.resolve(false);
                case 'SPECIAL_ASSIGNMENT':
                    drawerControls.open({
                        type: 'status-special-assignment',
                        props: { unitId, statusId: unitStateData.id },
                    });
                    return Promise.resolve(false);
                case 'VIRTUAL_PATROL':
                    drawerControls.open({
                        type: 'status-virtual-patrol',
                        props: { unitId, statusId: unitStateData.id },
                    });
                    return Promise.resolve(false);
                case 'TRANSPORT_EN_ROUTE':
                case 'HOSPITAL_TRANSPORT_EN_ROUTE':
                case 'HOSPITAL_TRANSPORT_ARRIVED':
                case 'TRANSPORT_ARRIVED':
                    drawerControls.open({
                        type: 'status-transport',
                        props: {
                            unitId,
                            unitStateId: unitStateData.id,
                            agencyEventId: eventIdToUse,
                        },
                    });
                    return Promise.resolve(false);
                case 'AVAILABLE':
                    // If unit is not assigned to an event, directly update status without release confirmation
                    if (!eventIdToUse) {
                        return executeStatusUpdate(args, eventIdToUse);
                    }
                    // Unit is assigned - show confirmation dialog before releasing
                    return releaseUnitWithConfirmation(args, eventIdToUse);
                default:
                    return executeStatusUpdate(args, eventIdToUse);
            }
        }
    );
}
